import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["onClick", "tabId"],
  _excluded2 = ["children", "className"],
  _excluded3 = ["children"];
import React, { useContext, useMemo, useEffect } from 'react';
import cn from 'classnames';
import { findIndex, isUndefined, reject } from 'lodash-es';
var warning = require('warning');
import { invokeAll, isElementOfType, isFunction } from '../../../library';
import { Menu } from '../../menu';
var tabClasses = {
  MENU: 'tab-menu'
};
var TabContext = React.createContext({
  activeTab: null,
  registerTab: function registerTab() {},
  setActiveTab: function setActiveTab() {},
  tabs: [],
  updateTab: function updateTab() {},
  unRegisterTab: function unRegisterTab() {}
});
var TabMenuItem = function TabMenuItem(props) {
  var _useContext = useContext(TabContext),
    activeTab = _useContext.activeTab,
    setActiveTab = _useContext.setActiveTab;
  var onClick = props.onClick,
    tabId = props.tabId,
    menuItemProps = _objectWithoutProperties(props, _excluded);
  function handleClick() {
    setActiveTab(props.tabId);
  }
  return React.createElement(Menu.Item, _extends({}, menuItemProps, {
    isActive: activeTab == props.tabId,
    onClick: invokeAll(handleClick, onClick)
  }));
};
;
export var TabMenu = function TabMenu(props) {
  var _useContext2 = useContext(TabContext),
    tabs = _useContext2.tabs;
  var children = props.children,
    className = props.className,
    menuProps = _objectWithoutProperties(props, _excluded2);
  var childMenuItems = useMemo(function () {
    return React.Children.toArray(children).filter(function (child) {
      var isValid = isElementOfType(child, TabMenuItem);
      if (!isValid) {
        warning(false, "TabMenu can only contain 'TabMenuItem' items. Child Ignored.");
      }
      return isValid;
    });
  }, [children]);
  return React.createElement(Menu, _extends({
    className: cn(tabClasses.MENU, className),
    appearance: "underline"
  }, menuProps), childMenuItems, tabs.map(function (props) {
    return React.createElement(TabMenuItem, _extends({
      key: props.tabId
    }, props));
  }));
};
TabMenu.displayName = 'TabMenu';
export var TabContent = function TabContent(props) {
  var _useContext3 = useContext(TabContext),
    activeTab = _useContext3.activeTab;
  function renderChildren() {
    if (isFunction(props.children)) {
      return props.children(activeTab);
    }
    return props.children;
  }
  if (isUndefined(props.tabId)) {
    return React.createElement(React.Fragment, null, renderChildren());
  }
  if (props.tabId === activeTab) {
    return React.createElement(React.Fragment, null, renderChildren());
  }
  return null;
};
TabContent.displayName = 'TabContent';
export var Tab = function Tab(props) {
  var children = props.children,
    tabProps = _objectWithoutProperties(props, _excluded3);
  var _useContext4 = useContext(TabContext),
    activeTab = _useContext4.activeTab,
    updateTab = _useContext4.updateTab,
    registerTab = _useContext4.registerTab,
    unRegisterTab = _useContext4.unRegisterTab;
  useEffect(function () {
    registerTab(tabProps);
    return function () {
      unRegisterTab(tabProps.tabId);
    };
  }, []);
  useEffect(function () {
    updateTab(tabProps);
  }, Object.values(tabProps));
  return activeTab === tabProps.tabId ? React.createElement("div", null, children) : null;
};
Tab.displayName = 'Tab';
export var Tabs = function Tabs(props) {
  var _props$defaultTab;
  var _React$useState = React.useState([]),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    tabs = _React$useState2[0],
    setTabs = _React$useState2[1];
  var _React$useState3 = React.useState((_props$defaultTab = props.defaultTab) !== null && _props$defaultTab !== void 0 ? _props$defaultTab : null),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    currentTab = _React$useState4[0],
    setCurrentTab = _React$useState4[1];
  var tabMenu = useMemo(function () {
    return React.Children.toArray(props.children).find(function (child) {
      return isElementOfType(child, TabMenu);
    });
  }, [props.children]);
  function registerTab(tabProps) {
    setTabs(function (curTabs) {
      return [].concat(_toConsumableArray(curTabs), [tabProps]);
    });
  }
  function updateTab(tabProps) {
    setTabs(function (cur) {
      var index = findIndex(cur, ['tabId', tabProps.tabId]);
      if (index < 0) return cur;
      var copy = _toConsumableArray(cur);
      copy.splice(index, 1, tabProps);
      return copy;
    });
  }
  ;
  function unRegisterTab(id) {
    setTabs(function (curTabs) {
      return reject(curTabs, ['id', id]);
    });
  }
  function setActiveTab(id) {
    var _props$onTabChange;
    setCurrentTab(id);
    (_props$onTabChange = props.onTabChange) === null || _props$onTabChange === void 0 || _props$onTabChange.call(props, id);
  }
  function getFirstTab() {
    if (!!tabMenu) {
      var tabItem = React.Children.toArray(tabMenu.props.children).find(function (child) {
        return isElementOfType(child, TabMenuItem);
      });
      if (tabItem) {
        return tabItem.props;
      } else {
        return tabs[0];
      }
    }
    return tabs[0];
  }
  function isControlled() {
    return props.activeTab !== undefined;
  }
  function getActiveTab() {
    var _ref, _getFirstTab;
    return isControlled() ? props.activeTab : (_ref = currentTab !== null && currentTab !== void 0 ? currentTab : (_getFirstTab = getFirstTab()) === null || _getFirstTab === void 0 ? void 0 : _getFirstTab.tabId) !== null && _ref !== void 0 ? _ref : null;
  }
  return React.createElement(React.Fragment, null, React.createElement(TabContext.Provider, {
    value: {
      activeTab: getActiveTab(),
      registerTab: registerTab,
      setActiveTab: setActiveTab,
      tabs: tabs,
      updateTab: updateTab,
      unRegisterTab: unRegisterTab
    }
  }, props.children));
};
Tabs.displayName = 'Tabs';
Tabs.Tab = Tab;
Tabs.Menu = TabMenu;
Tabs.MenuItem = TabMenuItem;
Tabs.Content = TabContent;